import {useEffect, useMemo, useState} from 'react';
import styles from './adminuser.module.scss';
import gridIcon from 'images/view-grid-outline.svg';
import listIcon from 'images/format-list-bulleted.svg';
import purpleGridIcon from 'images/grid-purple.svg';
import purpleListIcon from 'images/list-purple.svg';
import {ADMIN_USER_TYPES, NavTabsEnum} from 'utils/constants';
import {AdminUserList} from './AdminUserList';
import {Dropdown, Form, Input, Spin, Tooltip} from 'antd';
import DBModal from 'components/modal/modal';
import {DownloadOutlined} from '@ant-design/icons';
import {useLazyQuery} from '@apollo/client';
import {DOWNLOAD_ALL_USERS} from 'graphql/queries/users';
import {toast} from 'react-toastify';
import SubmitButton from 'components/Startup/components/SubmitButton';
import {useSelector} from 'react-redux';
import {makeSelectTabOperations} from 'redux/store/auth';

/**
 * Users.
 *
 * The stat strip and the KYC tabs are both derived from the same query the
 * list runs, so nothing here fetches on its own — `AdminUserList` hands the
 * statistics block back up through `onSetStatistics`.
 */
const AdminUser = () => {
  const [activeTab, setActiveTab] = useState('ALL');
  const [switchView, setSwitchView] = useState(false);
  const [page, setPage] = useState(1);
  const [loadingUsers, setLoadingUser] = useState(false);
  const [userStats, setUserStats] = useState({});
  const [downloadEmail, setDownloadEmail] = useState('');
  const [openModal, setOpenModal] = useState(false);
  const [currentUser, setCurrentUser] = useState(false);
  const [filterValues, setFilterValues] = useState({});

  const [userTypeMeta, setUserTypeMeta] = useState(
    ADMIN_USER_TYPES?.find(instance => instance?.key === activeTab),
  );

  const pageOperations = useSelector(state =>
    makeSelectTabOperations(state, NavTabsEnum.Users),
  );

  const handleNextPage = () => setPage(prevPage => prevPage + 1);
  const handlePreviousPage = () => setPage(currentPage => currentPage - 1);
  const handleResetPage = () => setPage(1);

  const [downloadUsers, {loading}] = useLazyQuery(DOWNLOAD_ALL_USERS, {
    fetchPolicy: 'no-cache',
  });

  useEffect(() => {
    setUserTypeMeta(() =>
      ADMIN_USER_TYPES?.find(instance => instance?.key === activeTab),
    );
  }, [activeTab]);

  const onMenuClick = ({key}) => {
    if (key === '1') {
      setCurrentUser(false);
      setOpenModal(true);
    }
    if (key === '2') {
      setOpenModal(true);
      setCurrentUser(true);
    }
  };

  const temporalMenu = [
    {
      label: `${activeTab === 'ALL' ? '' : 'All'} ${[
        userTypeMeta?.labelTitle,
      ]}`,
      key: '1',
    },
    {
      label: 'All Current Filtered User',
      key: '2',
    },
  ];

  const handleDownloadAllUsers = () => {
    downloadUsers({
      variables: {
        email: downloadEmail,
        match: {
          user: {
            ...(currentUser && Object.values(filterValues).length
              ? {...filterValues}
              : {
                  all: true,
                }),
            [userTypeMeta?.filterProperty]: userTypeMeta?.filterPropertyValue,
          },
        },
      },
    })
      .then(({data: {downloadAllUsers}}) => {
        if (!downloadAllUsers.response) {
          toast.error(downloadAllUsers.message);
        } else {
          toast.success('Successful, Please check your mail!');
          setOpenModal(false);
        }
      })
      .catch(err => toast.error(err));
  };

  /**
   * The stat strip.
   *
   * Each tile was a 250x160 block with its content in the top ~90px and dead
   * space below — the leftover of a card built for a percentage row that this
   * page never passes. They are plain tiles now, sized by the grid.
   */
  const stats = useMemo(
    () => [
      {
        key: 'total',
        label: 'Total users',
        value: userStats?.totalUsers,
        hint: 'every account, any status',
        accent: true,
      },
      {
        key: 'accredited',
        label: 'Accredited investors',
        value: userStats?.totalAccreditedUsers,
        hint: 'passed accreditation',
      },
      {
        key: 'active',
        label: 'Active investors',
        value: userStats?.totalActiveInvestors,
        hint: 'holding a position',
      },
      {
        key: 'verified',
        label: 'Verified users',
        value: userStats?.totalVerifiedInvestors,
        hint: 'KYC approved',
      },
    ],
    [userStats],
  );

  return (
    <div className={styles.container}>
      <section className={styles.header}>
        <div>
          <h1>Users</h1>
          <p className={styles.headerSub}>
            Every account on the platform, by KYC status.
          </p>
        </div>
        <div className={styles['right-grid']}>
          <span>Switch View: </span>
          <div className={styles.viewSwitch}>
            <Tooltip title="Card view">
              <button
                type="button"
                aria-label="Card view"
                aria-pressed={switchView}
                className={switchView ? styles.viewOn : ''}
                onClick={() => setSwitchView(true)}>
                <img src={switchView ? purpleGridIcon : gridIcon} alt="" />
              </button>
            </Tooltip>
            <Tooltip title="Table view">
              <button
                type="button"
                aria-label="Table view"
                aria-pressed={!switchView}
                className={!switchView ? styles.viewOn : ''}
                onClick={() => setSwitchView(false)}>
                <img src={switchView ? listIcon : purpleListIcon} alt="" />
              </button>
            </Tooltip>
          </div>
        </div>
      </section>

      <section className={styles['percentage-tab']}>
        {stats.map(stat => (
          <div
            key={stat.key}
            className={`${styles.statTile} ${
              stat.accent ? styles.statTileAccent : ''
            }`}>
            <span className={styles.statLabel}>{stat.label}</span>
            <span className={styles.statValue}>
              {stat.value != null
                ? new Intl.NumberFormat().format(stat.value)
                : '—'}
            </span>
            <span className={styles.statHint}>{stat.hint}</span>
          </div>
        ))}
      </section>

      <section className={styles['sections-tab']}>
        <div className={styles['verifier-toggler']}>
          {ADMIN_USER_TYPES.map(({label, key}) => (
            <button
              type="button"
              key={key}
              onClick={() => {
                // The search box used to be cleared by reaching into the DOM
                // for `#user-search`. It is a controlled input now, and the
                // list clears it from state when the tab changes.
                setPage(1);
                setActiveTab(key);
              }}
              className={activeTab === key ? styles['active-tab'] : ''}>
              {label}
            </button>
          ))}
        </div>
        <div className={styles['download-icon']}>
          {pageOperations?.export && (
            <Dropdown
              trigger="click"
              menu={{
                items: temporalMenu,
                onClick: onMenuClick,
              }}
              placement="bottomRight">
              {/* No Tooltip wrapper here: Dropdown clones its child as the
                  trigger, and a Tooltip in between clones it again, which
                  dropped this className and with it the button's styling. */}
              <div className={styles['select-btn']} title="Export users">
                <DownloadOutlined />
              </div>
            </Dropdown>
          )}
        </div>
        <DBModal
          isOpen={openModal}
          handleClose={() => setOpenModal(false)}
          content={
            <div className={styles.userDownloadModal}>
              {loadingUsers || loading ? (
                <div className={styles.modalLoader}>
                  <Spin size="large" />
                  <h1>Loading.... Please wait</h1>
                </div>
              ) : (
                <div className={styles.modalContent}>
                  <div className={styles.modalHeader}>
                    <h1>Download Users</h1>
                    <span>
                      Download a CSV file of{' '}
                      {currentUser ? 'Current Page' : 'All'} USERS to your Daba
                      Email. Please Note the mail might be in your spam folder
                    </span>
                  </div>
                  <Form onFinish={handleDownloadAllUsers} layout="vertical">
                    <Form.Item name="email" label="Email">
                      <Input
                        placeholder="admin@joindaba.com"
                        style={{width: 400}}
                        value={downloadEmail}
                        onChange={e => setDownloadEmail(e.target.value)}
                      />
                    </Form.Item>
                    <SubmitButton label="Download" />
                  </Form>
                </div>
              )}
            </div>
          }
        />
      </section>

      <section>
        <AdminUserList
          switchView={switchView}
          userType={activeTab}
          userTypeMeta={userTypeMeta}
          onLoadingUser={setLoadingUser}
          setCurrentPage={setPage}
          currentPage={page}
          onNextPage={handleNextPage}
          onPreviousPage={handlePreviousPage}
          operations={pageOperations}
          onPageReset={handleResetPage}
          onSetStatistics={setUserStats}
          filterValues={filterValues}
          setFilterValues={setFilterValues}
        />
      </section>
    </div>
  );
};

export {AdminUser};
